/**
 * Snapshot genérico en localStorage para el backend Dexie de desarrollo.
 *
 * Permite "congelar" el estado actual de una colección de entidades como
 * configuración por defecto del PMIS: seed.ts la recarga automáticamente
 * cuando la tabla está vacía (tras un reset de la base de datos). Es la
 * herramienta para armar y conservar escenarios de prueba.
 *
 * Cada entidad expone su propio store con una clave de almacenamiento
 * distinta (ver soDefault.ts, rdDefault.ts, contratosDefault.ts...).
 */

/** Operaciones de un snapshot persistido en localStorage. */
export interface SnapshotStore<T> {
  /** Devuelve el snapshot guardado, o null si no existe / es inválido. */
  leer(): T[] | null;
  /** Guarda el array completo como snapshot. */
  guardar(items: T[]): void;
  /** Elimina el snapshot guardado. */
  borrar(): void;
  /** Indica si hay un snapshot guardado. */
  existe(): boolean;
}

/**
 * Crea un store de snapshot para una entidad con `id: string`.
 * @param storageKey clave única de localStorage, p. ej. "pmis.sosDefault".
 */
export function crearSnapshotStore<T extends { id: string }>(
  storageKey: string,
): SnapshotStore<T> {
  return {
    leer(): T[] | null {
      if (typeof localStorage === "undefined") return null;
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return null;
        const ok = parsed.every(
          (x) =>
            x !== null &&
            typeof x === "object" &&
            "id" in x &&
            typeof (x as { id: unknown }).id === "string",
        );
        return ok ? (parsed as T[]) : null;
      } catch {
        return null;
      }
    },
    guardar(items: T[]): void {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(storageKey, JSON.stringify(items));
    },
    borrar(): void {
      if (typeof localStorage === "undefined") return;
      localStorage.removeItem(storageKey);
    },
    existe(): boolean {
      if (typeof localStorage === "undefined") return false;
      return localStorage.getItem(storageKey) !== null;
    },
  };
}
